// Ambient effects: motion the board draws over a theme's picture or scene, all
// made to one standard, the drawn Aurora's: slow, soft, few, glowing, and quiet
// in the middle of the screen where the tiles sit. A theme names one, or two
// that belong together, or none, and most pictures look best with none. An
// agent picks by these words; src/themes/effects draws them.

import type { EffectKind, ParticleKind, ThemeEffect } from "./types";

export type EffectGroup = "Sky" | "Air" | "Light" | "Camera";

export interface EffectInfo {
  kind: EffectKind;
  name: string;
  group: EffectGroup;
  /** what it looks like, in a few words */
  says: string;
  /** the pictures it belongs with */
  suits: string;
}

export const EFFECTS: EffectInfo[] = [
  { kind: "aurora", name: "Aurora", group: "Sky", says: "curtains of northern light ripple slowly across the top of the sky", suits: "a dark night sky over mountains, snow or water" },
  { kind: "stars", name: "Starlight", group: "Sky", says: "the stars in a dark sky brighten and soften, slowly", suits: "any clear night sky" },
  { kind: "meteors", name: "Shooting stars", group: "Sky", says: "now and then a shooting star crosses the sky", suits: "a dark sky with room above the horizon" },
  { kind: "lightning", name: "Distant lightning", group: "Sky", says: "now and then the sky lights up softly from a storm far away", suits: "storm clouds, a dark sea, rain at night" },
  { kind: "clouds", name: "Cloud shadows", group: "Sky", says: "the shadows of passing clouds drift slowly over the land", suits: "daylight over hills, fields, dunes or a valley; y is where the land starts" },
  { kind: "mist", name: "Mist", group: "Air", says: "banks of mist drift slowly, low across the scene", suits: "a lake, a valley, a forest or a shore at dawn or dusk; y is where it hangs" },
  { kind: "rain", name: "Light rain", group: "Air", says: "fine, sparse rain", suits: "a wet street, a city at night, a grey shore" },
  { kind: "snow", name: "Snowfall", group: "Air", says: "soft flakes falling slowly, the nearest out of focus", suits: "snow, a winter forest, a cabin, a city in winter" },
  { kind: "fireflies", name: "Fireflies", group: "Air", says: "a few points of light glow, fade and wander low down", suits: "a meadow, a forest or a garden at dusk or night" },
  { kind: "motes", name: "Drifting dust", group: "Air", says: "specks of light drift slowly and catch the light", suits: "paintings, golden light, warm haze, an old room" },
  { kind: "embers", name: "Rising embers", group: "Air", says: "sparks rise slowly from below and fade", suits: "a fire, a volcano, a forge; x and y are where they rise from" },
  { kind: "petals", name: "Falling petals", group: "Air", says: "a few petals tumble slowly past", suits: "cherry blossom, a garden in spring" },
  { kind: "leaves", name: "Falling leaves", group: "Air", says: "a few leaves tumble slowly past", suits: "an autumn forest or park" },
  { kind: "bubbles", name: "Rising bubbles", group: "Air", says: "a few bubbles rise slowly", suits: "under water, a reef, a deep blue" },
  { kind: "sunbeams", name: "Sunbeams", group: "Light", says: "soft shafts of light reach down from above, with dust turning in them", suits: "sun through trees, mist or a window; x and y are where the light comes from" },
  { kind: "glow", name: "Breathing glow", group: "Light", says: "a light in the scene swells and softens, slowly", suits: "a low sun, a lamp, a fire, lava, a lit window; x and y are where it is" },
  { kind: "glints", name: "Glints on water", group: "Light", says: "light catches the water in small, brief sparkles", suits: "a sea, a lake or a river with light on it" },
  { kind: "drift", name: "Slow drift", group: "Camera", says: "the picture itself moves, a slow zoom and pan, like a film of a still", suits: "any picture, alone or with one other effect" },
];

export const EFFECT_KINDS: EffectKind[] = EFFECTS.map((e) => e.kind);
const BY_KIND: ReadonlyMap<string, EffectInfo> = new Map(EFFECTS.map((e) => [e.kind, e]));
export const effectInfo = (kind: string): EffectInfo | undefined => BY_KIND.get(kind);

/** A theme carries one effect, or two that belong together. */
export const MAX_EFFECTS = 2;

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const unit = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : undefined);

/** Effects from anywhere, made safe to keep: a list, a single one, or just a name ("mist") all work. */
export function cleanEffects(raw: unknown): ThemeEffect[] {
  const list = Array.isArray(raw) ? raw : raw === undefined || raw === null ? [] : [raw];
  const out: ThemeEffect[] = [];
  for (const e of list) {
    const o: Record<string, unknown> = typeof e === "string" ? { kind: e } : e && typeof e === "object" && !Array.isArray(e) ? (e as Record<string, unknown>) : {};
    const kind = typeof o.kind === "string" ? o.kind.trim().toLowerCase() : "";
    if (!BY_KIND.has(kind) || out.some((x) => x.kind === kind)) continue;
    const strength = unit(o.strength);
    const color = typeof o.color === "string" && HEX.test(o.color) ? o.color.toLowerCase() : undefined;
    const x = unit(o.x);
    const y = unit(o.y);
    out.push({
      kind: kind as EffectKind,
      ...(strength !== undefined ? { strength } : {}),
      ...(color ? { color } : {}),
      ...(x !== undefined ? { x } : {}),
      ...(y !== undefined ? { y } : {}),
    });
    if (out.length >= MAX_EFFECTS) break;
  }
  return out;
}

/** What the cleaner would drop, in plain words, so an agent can choose again rather than find it missing. */
export function effectsLeftOut(raw: unknown): string[] {
  const list = Array.isArray(raw) ? raw : raw === undefined || raw === null ? [] : [raw];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const e of list) {
    const kind = typeof e === "string" ? e : e && typeof e === "object" ? (e as { kind?: unknown }).kind : undefined;
    const k = typeof kind === "string" ? kind.trim().toLowerCase() : "";
    if (!BY_KIND.has(k)) out.push(`the effect ${JSON.stringify(String(kind ?? ""))}, since the effects are ${EFFECT_KINDS.join(", ")}`);
    else if (seen.has(k)) out.push(`the second ${JSON.stringify(k)}, since a theme carries each effect once`);
    else if (seen.add(k).size > MAX_EFFECTS) out.push(`the effect ${JSON.stringify(kind)}, since a theme carries ${MAX_EFFECTS} at most`);
  }
  return out;
}

/**
 * The effect a theme made before effects existed meant by its scene's
 * particles, when it shows a picture: the falling things it asked for, drawn
 * the new way, at the density it chose.
 */
export function effectFromParticles(p: { kind: ParticleKind; color?: string; density?: number } | undefined): ThemeEffect | null {
  if (!p) return null;
  const kind: EffectKind | null = p.kind === "dust" ? "motes" : (EFFECT_KINDS as string[]).includes(p.kind) ? (p.kind as EffectKind) : null;
  if (!kind) return null;
  return { kind, ...(typeof p.density === "number" ? { strength: Math.max(0, Math.min(1, p.density)) } : {}), ...(p.color && HEX.test(p.color) ? { color: p.color } : {}) };
}
